import { useEffect } from "react";
import { useRoomContext } from "@livekit/components-react";
import { DisconnectReason, RoomEvent } from "livekit-client";
import { useLiveSettingsActions } from "../../store";
import type { ProviderNotificationsListenerOptions } from "../types";
import { ACTION_TYPES } from "./helpers";

export const useLivekitNotificationsListener = ({
  onRoomUpdate,
  onEndStream,
}: ProviderNotificationsListenerOptions) => {
  const room = useRoomContext();
  const { setMicrophoneEnabled, setCameraEnabled } = useLiveSettingsActions();

  useEffect(() => {
    const decoder = new TextDecoder();
    const onDataReceived = (data: Uint8Array) => {
      try {
        const strData = decoder.decode(data);
        const action = JSON.parse(strData);
        const { type } = action || {};
        switch (type) {
          case ACTION_TYPES.UPDATE_ROOM:
            onRoomUpdate && onRoomUpdate();
            break;
          case ACTION_TYPES.MUTE_MICROPHONE:
            setMicrophoneEnabled(false);
            break;
          case ACTION_TYPES.DISABLE_CAMERA:
            setCameraEnabled(false);
            break;
        }
      } catch (err: any) {
        console.error("Could not decode received data - ", err.message);
      }
    };

    const onRoomDisconnected = (reason?: DisconnectReason) => {
      if (
        reason === DisconnectReason.ROOM_DELETED ||
        reason === DisconnectReason.PARTICIPANT_REMOVED
      ) {
        onEndStream && onEndStream();
      }
    };

    if (room) {
      room.on(RoomEvent.DataReceived, onDataReceived);
      room.on(RoomEvent.Disconnected, onRoomDisconnected);
    }

    return () => {
      if (room) {
        room.off(RoomEvent.DataReceived, onDataReceived);
        room.off(RoomEvent.Disconnected, onRoomDisconnected);
      }
    };
  }, [onEndStream, onRoomUpdate, room, setMicrophoneEnabled]);
};
